import { ApolloError } from '@apollo/client'
import React, { forwardRef, PropsWithChildren, useContext } from 'react'
import styled from 'styled-components'
import { AppDataContext } from '../context/AppDataContext'
import { Checkbox } from './Checkbox'
import { Flex, FlexingBox, HeadRow } from './Flex'
import { MaybeLoading } from './PageLoader'
import { TitleWithSub } from './TitleWithSub'
import { FilterCategory } from './filter/filter-category/FilterCategory'
import { Category, RefernceValueFilter } from '../graphql/generated'
import { IFilterDatePeriod } from '../utils/datePeriod'
import { SelectYear } from './filter/SelectYear'
import { spacing } from '../theme'

const ControlsRow = styled(Flex)`
  flex-direction: row;
  align-items: center;
  border-top: 1px solid #b0b0b0;
  padding: ${spacing.large};
  max-height: 64px;
  gap: ${spacing.medium};
`

const Container = styled(FlexingBox)`
  overflow: visible;
  min-height: 380px;
`

type Props = {
  ControlsStart?: React.ReactNode
  ControlsEnd?: React.ReactNode
  titleData: { title: string; subtitle: string }
  /**
   * Current selection for the reference value.
   * The caller should update this when `setReferenceFilter` is called.
   */
  referenceFilter?: RefernceValueFilter
  /**
   * If this callback is provided, the card footer displays an element to toggle
   * the reference value.
   */
  setReferenceFilter?: (x: RefernceValueFilter) => void
  /**
   * Any reference value types included here will not be displayed as selections,
   * even if the org has data to support it.
   */
  forbiddenRefFilters?: Set<RefernceValueFilter>
  loading: boolean
  error?: ApolloError | Error
  errorMessageWhenNoData?: string
  hasData: boolean
  loadingPlaceholderHeight?: number
  tooltip?: React.JSX.Element
  contentWrapperStyle?: React.CSSProperties
  categories?: Category[]
  selectedCategory?: Category
  setCategory?: (cat?: Category) => void
  selectedDatePeriod?: IFilterDatePeriod
  setDatePeriod?: React.Dispatch<React.SetStateAction<IFilterDatePeriod>>
  className?: string
  controlsStyle?: React.CSSProperties
}

// TODO: Allow style overrides.
export const CardTemplate = forwardRef<
  HTMLDivElement,
  PropsWithChildren<Props>
>(
  (
    {
      ControlsStart,
      ControlsEnd,
      titleData,
      referenceFilter,
      setReferenceFilter,
      forbiddenRefFilters,
      children,
      tooltip,
      errorMessageWhenNoData,
      contentWrapperStyle,
      selectedCategory,
      setCategory,
      selectedDatePeriod,
      setDatePeriod,
      className,
      controlsStyle,
      ...props
    },
    ref,
  ) => {
    const { organisation, orgHasInitializedData } = useContext(AppDataContext)

    // We can show annual work force if the org has data for it, and
    // the card using the template does not forbid it.
    const canShowAWF =
      organisation.useAnnualWorkforce &&
      !forbiddenRefFilters?.has(RefernceValueFilter.AnnualWorkForce)

    // We can show revenue intensity if the org has data for it, and
    // the card using the template does not forbid it.
    const canShowRevenue =
      organisation.hasRevenueData &&
      !forbiddenRefFilters?.has(RefernceValueFilter.Revenue)

    // Determine which kind of reference value toggle to display (if any).
    // For now, only one reference value toggle is shown --
    // "annual work force" takes precedence over "intensity per revenue".
    let refValToShow: RefernceValueFilter = RefernceValueFilter.None
    if (!setReferenceFilter) {
      // No setter provided, card cannot toggle ref value - no checkbox.
      refValToShow = RefernceValueFilter.None
    } else if (canShowAWF) {
      // Has setter, has AWF data and is allowed to show it - show AWF.
      refValToShow = RefernceValueFilter.AnnualWorkForce
    } else if (canShowRevenue) {
      // Has setter, not showing AWF, has revenue data, is allowed to show it - show revenue.
      refValToShow = RefernceValueFilter.Revenue
    }

    const showControlsRow = Boolean(
      refValToShow !== RefernceValueFilter.None || // not None = show a checkbox
        (!!setDatePeriod && !!selectedDatePeriod) ||
        !!setCategory ||
        ControlsStart ||
        ControlsEnd,
    )
    const { categories } = useContext(AppDataContext)
    const headCategories = categories.filter((x) => x?.children.length > 0)

    return (
      <Container ref={ref} className={className}>
        <HeadRow>
          <TitleWithSub
            title={titleData.title}
            sub={titleData.subtitle}
            infoDescription={tooltip}
          />
        </HeadRow>
        <MaybeLoading
          orgHasInitializedData={orgHasInitializedData}
          {...props}
          defaultErrorMessage={errorMessageWhenNoData}
        >
          <span style={{ flex: 1, ...(contentWrapperStyle ?? {}) }}>
            {children}
          </span>
        </MaybeLoading>
        {showControlsRow && (
          <ControlsRow
            data-testid='card-template-controls-row'
            style={controlsStyle}
          >
            {ControlsStart}
            {!!setCategory && (
              <FilterCategory
                selectedCategory={selectedCategory}
                setCategory={setCategory}
                categories={headCategories}
              />
            )}
            <ReferenceValueCheckbox
              kind={refValToShow}
              referenceFilter={referenceFilter}
              setReferenceFilter={setReferenceFilter}
            />
            {!!setDatePeriod && !!selectedDatePeriod && (
              <SelectYear
                selectedDatePeriod={selectedDatePeriod}
                setDatePeriod={setDatePeriod}
              />
            )}
            {ControlsEnd}
          </ControlsRow>
        )}
      </Container>
    )
  },
)

function ReferenceValueCheckbox({
  kind,
  referenceFilter,
  setReferenceFilter,
}: { kind: RefernceValueFilter } & Pick<
  Props,
  'referenceFilter' | 'setReferenceFilter'
>): React.JSX.Element | null {
  const labelByKind: { [kind in RefernceValueFilter]: string } = {
    [RefernceValueFilter.None]: '',
    [RefernceValueFilter.AnnualWorkForce]: 'Visa per anställd',
    [RefernceValueFilter.Revenue]: 'Visa per omsättning',
  }
  if (kind === RefernceValueFilter.None) {
    return null
  }
  return (
    <Checkbox
      data-testid='card-template-refval-checkbox'
      // checked, if the selected value is the same as the value of the filter kind.
      isChecked={referenceFilter === kind}
      label={labelByKind[kind]}
      // Component is unstable and will re-render on every render, but since it is only displayed during
      // mouse-over and should therefore not be displayed while the parent is changed, this is not a concern.
      // eslint-disable-next-line react/no-unstable-nested-components
      titleTooltipGenerator={() => <TooltipComponent kind={kind} />}
      onChange={() => {
        if (referenceFilter === kind) {
          setReferenceFilter?.(RefernceValueFilter.None)
        } else {
          setReferenceFilter?.(kind)
        }
      }}
    />
  )
}

function TooltipComponent({
  kind,
}: {
  kind: RefernceValueFilter
}): React.JSX.Element | null {
  if (kind === RefernceValueFilter.AnnualWorkForce) {
    return (
      <>
        <span>Antalet anställda representeras av antalet </span>
        <span style={{ fontStyle: 'italic' }}>årsarbetskrafter</span>
        <span>
          {' '}
          (heltidstjänster över ett år). Ex: två halvtidstjänster som sträcker
          sig över ett år blir tillsammans en årsarbetskraft.
        </span>
      </>
    )
  } else if (kind === RefernceValueFilter.Revenue) {
    return <span>Visa utsläppsintensitet per omsättning</span>
  }

  return null
}

export default CardTemplate
